/* ============================================================================
 * Tykables Product Reviews — storefront component
 * ----------------------------------------------------------------------------
 * <tyk-review-form data-product-id data-customer-id>
 *   Rendered ONLY for logged-in customers (sections/tyk-product-reviews.liquid
 *   never outputs the element for guests). On connect it asks the backend
 *   whether this customer may review this product:
 *
 *     GET  /apps/pwa/reviews/eligible?product_id=<id>
 *       → { eligible: true, verified: true|false }
 *       → { eligible: false, reason: 'already_reviewed' | ... }
 *
 *   Since 2026-07-31 (CHG-20260731-06) any logged-in customer is eligible
 *   unless they already reviewed the product — the purchase/delivery check is
 *   a verified_buyer annotation on the backend, not a gate. The old
 *   not_purchased / not_delivered copy is kept for backward compatibility
 *   while older backend revisions might still serve.
 *
 *   Only an eligible:true response renders the form. Submission:
 *
 *     POST /apps/pwa/reviews
 *       { product_id, rating, title, body, fit }
 *       → 201 { ok: true }   (review lands PENDING; moderation publishes)
 *       → 4xx { ok: false, error }
 *
 *   Both calls ride the /apps/pwa App Proxy, so Shopify signs the request and
 *   injects logged_in_customer_id server-side — customer identity is never
 *   taken from the DOM/client. data-customer-id exists only as a render gate.
 *
 * Also wires the "Show more reviews" button on the SSR list.
 *
 * IDENTITY MODEL — logged-in only (matches tyk-poll.js / project memory
 * polls_logged_in_only): no customerId in window.tykReviewsConfig ⇒ no-op.
 *
 * Spinner contract: submit button includes .loading__spinner and the form
 * includes .product-form__error-message-wrapper so existing Dawn CSS handles
 * loading/error state (memory: product_form_spinner_contract — applied here
 * for consistency even though we're not inside <product-form>).
 *
 * Edit log:
 *   - 2026-07-31  CHG-20260731-06  John-Michael  Title/body fields gained
 *     Dawn floating labels (.field__label) — Dawn CSS hides native
 *     placeholders on .field__input, so the fields rendered as unlabeled
 *     empty boxes. Also documented the annotation-only eligibility model.
 *   - 2026-07-31  CHG-20260731-07  John-Michael  Title field removed (body
 *     only); form fields restyled via tyk-reviews__* classes instead of
 *     Dawn .field (double-border fix); section background made transparent
 *     in tykables-pdp.css.
 *   - 2026-08-12  CHG-20260810-02  Ineligibility and network failures now render
 *     explanatory copy instead of an empty region; added copy for not_logged_in
 *     / no_product / bad_signature. Reconciled from the live 7.1.2 theme, which
 *     was AHEAD of this repo by CHG-20260731-06/07.
 * ==========================================================================*/

(function () {
  'use strict';

  if (window.customElements && customElements.get('tyk-review-form')) return;

  var cfg = window.tykReviewsConfig || {};
  var PROXY_BASE = cfg.proxyBase || '/apps/pwa/reviews';

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'text') node.textContent = attrs[k];
        else if (k === 'html') node.innerHTML = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) { node.appendChild(c); });
    return node;
  }

  var INELIGIBLE_COPY = {
    not_purchased: 'Reviews are limited to verified purchasers. Once you’ve ordered this product and it’s been delivered, you can review it here.',
    not_delivered: 'Thanks for your order! You’ll be able to review this product once your delivery is confirmed.',
    already_reviewed: 'You’ve already reviewed this product — thank you!',
    not_logged_in: 'Please sign in to write a review.',
    no_product: 'We couldn’t match this page to a product. Try refreshing.',
    bad_signature: 'We couldn’t verify your session. Try refreshing the page.'
  };

  // CHG-20260810-02: an unrecognised reason (or any network/5xx failure) used to
  // render NOTHING, so the compose area was an unexplained empty box and the
  // failure was undiagnosable from the front end.
  var GENERIC_COPY = 'Reviews are temporarily unavailable. Please try again shortly.';

  var FIT_OPTIONS = [
    { value: 'runs_small', label: 'Runs small' },
    { value: 'true_to_size', label: 'True to size' },
    { value: 'runs_large', label: 'Runs large' }
  ];

  // --------------------------------------------------------------------------
  // <tyk-review-form>
  // --------------------------------------------------------------------------
  function defineReviewForm() {
    function C() { return Reflect.construct(HTMLElement, [], C); }
    C.prototype = Object.create(HTMLElement.prototype);
    C.prototype.constructor = C;

    C.prototype.connectedCallback = function () {
      if (!cfg.customerId) return; // logged-in only — hard stop
      this.productId = this.dataset.productId;
      if (!this.productId) return;
      this.checkEligibility();
    };

    C.prototype.checkEligibility = function () {
      var self = this;
      fetch(PROXY_BASE + '/eligible?product_id=' + encodeURIComponent(this.productId), {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (data) {
          if (data && data.eligible) self.renderForm();
          else self.renderIneligible(data && data.reason);
        })
        .catch(function (err) {
          // CHG-20260810-02: surface the failure rather than leaving an empty box.
          self.textContent = '';
          self.appendChild(el('p', { class: 'tyk-reviews__ineligible', text: GENERIC_COPY }));
          if (window.console && console.warn) console.warn('[tyk-reviews] eligibility check failed:', err);
        });
    };

    C.prototype.renderIneligible = function (reason) {
      this.textContent = '';
      var copy = INELIGIBLE_COPY[reason] || GENERIC_COPY;
      this.appendChild(el('p', { class: 'tyk-reviews__ineligible', text: copy }));
      if (!INELIGIBLE_COPY[reason] && window.console && console.warn) {
        console.warn('[tyk-reviews] unrecognised ineligibility reason:', reason);
      }
    };

    C.prototype.renderForm = function () {
      var self = this;
      this.textContent = '';

      var form = el('form', { class: 'tyk-reviews__form', novalidate: 'novalidate' });

      form.appendChild(el('h3', { class: 'tyk-reviews__form-heading', text: 'Write a review' }));

      // Star rating (radio group rendered as stars)
      var ratingWrap = el('fieldset', { class: 'tyk-reviews__rating-input' });
      ratingWrap.appendChild(el('legend', { text: 'Your rating' }));
      var starRow = el('div', { class: 'tyk-reviews__rating-stars', role: 'radiogroup' });
      for (var i = 1; i <= 5; i++) {
        var id = 'tyk-review-star-' + i;
        var input = el('input', { type: 'radio', name: 'rating', value: String(i), id: id, required: 'required' });
        var label = el('label', { for: id, 'aria-label': i + ' star' + (i > 1 ? 's' : '') });
        label.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l2.1 4.3 4.7.7-3.4 3.3.8 4.7L8 12.3 3.8 14.5l.8-4.7L1.2 6.5l4.7-.7z"/></svg>';
        starRow.appendChild(input);
        starRow.appendChild(label);
      }
      ratingWrap.appendChild(starRow);
      form.appendChild(ratingWrap);

      // Review text — self-styled (tyk-reviews__*) rather than Dawn's .field
      // float-label machinery, which fought the site-wide input skin and
      // produced double borders (CHG-20260731-07).
      var bodyField = el('div', { class: 'tyk-reviews__field' });
      bodyField.appendChild(el('label', {
        class: 'tyk-reviews__label', for: 'tyk-review-body', text: 'Your review'
      }));
      var ta = el('textarea', {
        name: 'body', id: 'tyk-review-body', class: 'tyk-reviews__textarea',
        rows: '4', maxlength: '2000', required: 'required',
        placeholder: 'What did you think?'
      });
      bodyField.appendChild(ta);
      form.appendChild(bodyField);

      // Fit
      var fitWrap = el('fieldset', { class: 'tyk-reviews__fit-input' });
      fitWrap.appendChild(el('legend', { text: 'How does it fit?' }));
      FIT_OPTIONS.forEach(function (opt) {
        var id = 'tyk-review-fit-' + opt.value;
        var lab = el('label', { class: 'tyk-reviews__fit-option', for: id });
        lab.appendChild(el('input', { type: 'radio', name: 'fit', value: opt.value, id: id }));
        lab.appendChild(el('span', { text: opt.label }));
        fitWrap.appendChild(lab);
      });
      form.appendChild(fitWrap);

      // Error region (Dawn-styled)
      var errWrap = el('div', { class: 'product-form__error-message-wrapper', role: 'alert', hidden: 'hidden' });
      errWrap.appendChild(el('span', { class: 'product-form__error-message' }));
      form.appendChild(errWrap);

      // Submit (spinner contract)
      var btn = el('button', { type: 'submit', class: 'button button--primary tyk-reviews__submit' });
      btn.appendChild(el('span', { text: 'Submit review' }));
      var spinner = el('div', { class: 'loading__spinner hidden' });
      spinner.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
      btn.appendChild(spinner);
      form.appendChild(btn);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit(form, btn, errWrap);
      });

      this.appendChild(form);
    };

    C.prototype.submit = function (form, btn, errWrap) {
      var self = this;
      var rating = form.querySelector('input[name="rating"]:checked');
      var body = form.querySelector('textarea[name="body"]').value.trim();
      var fit = form.querySelector('input[name="fit"]:checked');

      var showError = function (msg) {
        errWrap.removeAttribute('hidden');
        errWrap.querySelector('.product-form__error-message').textContent = msg;
      };
      errWrap.setAttribute('hidden', 'hidden');

      if (!rating) return showError('Please choose a star rating.');
      if (!body) return showError('Please write a few words about the product.');

      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('loading');
      btn.querySelector('.loading__spinner').classList.remove('hidden');

      fetch(PROXY_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          product_id: this.productId,
          rating: parseInt(rating.value, 10),
          title: '',
          body: body,
          fit: fit ? fit.value : null
        })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (res.ok && res.json && res.json.ok) {
            self.textContent = '';
            self.appendChild(el('p', {
              class: 'tyk-reviews__thanks',
              text: 'Thanks! Your review has been submitted and will appear once it’s approved.'
            }));
          } else {
            var msg = (res.json && res.json.error) || 'Something went wrong — please try again.';
            showError(msg);
          }
        })
        .catch(function () {
          showError('Network error — please try again.');
        })
        .finally(function () {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading');
          var sp = btn.querySelector('.loading__spinner');
          if (sp) sp.classList.add('hidden');
        });
    };

    return C;
  }

  if (window.customElements) {
    customElements.define('tyk-review-form', defineReviewForm());
  }

  // --------------------------------------------------------------------------
  // "Show more reviews" — reveals the next page of SSR'd, hidden <li>s.
  // --------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var moreBtn = e.target.closest ? e.target.closest('[data-tyk-reviews-more]') : null;
    if (!moreBtn) return;
    var list = document.querySelector('[data-tyk-review-list]');
    if (!list) return;
    var pageSize = parseInt(list.getAttribute('data-page-size'), 10) || 6;
    var hidden = list.querySelectorAll('li[hidden]');
    for (var i = 0; i < Math.min(pageSize, hidden.length); i++) {
      hidden[i].removeAttribute('hidden');
    }
    if (list.querySelectorAll('li[hidden]').length === 0) {
      moreBtn.remove();
    }
  });
})();
